// ============================================================================
// ParentPickerPopover — rule-aware parent picker rendered from the checklist
// breadcrumb. Lists workspace-default + every GOAL/TASK/SUBTASK annotated
// with its verdict so ineligible entries render disabled with the reason as
// tooltip. On select, calls back and (optionally) shows a "Clamp checklist to
// parent window" fix when the caller returned `needs-clamp`.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from 'react'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { TextInput } from '../Common'
import {
  listEligibleParents,
  type ChecklistParentType,
  type ChecklistWindow,
} from '../../utils/checklistParentRules'
import type { TimelineElement, TimelineElementType } from '../../types/timelineElement'
import type { ChecklistParentSaveOutcome } from '../../types/checklistDetailController'

export type ParentPickerContext = {
  checklistType: ChecklistParentType
  window: ChecklistWindow | null
  candidates: TimelineElement[]
  workspaceDefaultContainerId: string
  currentParentId: string | null
}

export type WorkItemPickerItem = Pick<
  TimelineElement,
  'id' | 'type' | 'title' | 'startDate' | 'endDate'
> | {
  id: string
  type: 'CHECKLIST'
  title: string
  startDate?: string | null
  endDate?: string | null
}

type SharedProps = {
  anchorRef: React.RefObject<HTMLElement | null>
  onClose: () => void
}

type ReparentProps = SharedProps & {
  mode?: 'reparent'
  onReparent: (
    nextParentId: string | null,
    opts?: { window?: ChecklistWindow | null },
  ) => Promise<ChecklistParentSaveOutcome>
  context: ParentPickerContext
}

type SelectPropsBase = SharedProps & {
  mode: 'select'
  candidates: WorkItemPickerItem[]
  currentItemId?: string | null
}

type SingleSelectProps = SelectPropsBase & {
  multiSelect?: false | undefined
  onSelect: (item: WorkItemPickerItem) => void
}

type MultiSelectProps = SelectPropsBase & {
  multiSelect: true
  onSelect?: never
  onCommit: (items: WorkItemPickerItem[]) => void
  initialSelectedIds?: string[]
  maxSelect?: number
}

type SelectProps = SingleSelectProps | MultiSelectProps

export type ParentPickerPopoverProps = ReparentProps | SelectProps

const KIND_LABEL: Record<TimelineElementType | 'CHECKLIST', string> = {
  GOAL: 'Goal',
  TASK: 'Task',
  SUBTASK: 'Subtask',
  CHECKLIST: 'Checklist',
}

export const ParentPickerPopover = (props: ParentPickerPopoverProps) => {
  const { anchorRef, onClose } = props
  const [query, setQuery] = useState('')
  const [selectedItems, setSelectedItems] = useState<WorkItemPickerItem[]>(() => {
    if (props.mode !== 'select' || !props.multiSelect) return []
    const selectedIds = new Set(props.initialSelectedIds)
    return props.candidates.filter((candidate) => selectedIds.has(candidate.id))
  })
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [clampPrompt, setClampPrompt] = useState<{
    parentId: string | null
    clampTo: ChecklistWindow
    reason: string
  } | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Close on outside click / Escape.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (rootRef.current?.contains(target)) return
      if (anchorRef.current?.contains(target)) return
      onClose()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [anchorRef, onClose])

  type PickerRow = {
    element: TimelineElement | WorkItemPickerItem | null
    verdict: { ok: boolean; reason?: string }
  }

  const rows = useMemo<PickerRow[]>(() => {
    if (props.mode === 'select') {
      return props.candidates.map((element) => ({ element, verdict: { ok: true } }))
    }
    return listEligibleParents({
      candidates: props.context.candidates,
      checklistType: props.context.checklistType,
      window: props.context.window,
      workspaceDefaultContainerId: props.context.workspaceDefaultContainerId,
    })
  }, [props])

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((row) => {
      const label = row.element ? (row.element.title || 'Untitled') : 'Workspace (no parent)'
      return label.toLowerCase().includes(q)
    })
  }, [query, rows])

  const eligibleCount = props.mode !== 'select' ? rows.filter((row) => row.verdict.ok).length : null

  const toggleSelectedItem = (item: WorkItemPickerItem) => {
    setSelectedItems((current) => {
      const isSelected = current.some((selected) => selected.id === item.id)
      if (isSelected) return current.filter((selected) => selected.id !== item.id)
      if (props.mode !== 'select' || !props.multiSelect) return current
      if (props.maxSelect !== undefined && current.length >= props.maxSelect) return current
      return [...current, item]
    })
  }

  const applySelection = async (
    nextParentId: string | null,
    windowOverride?: ChecklistWindow | null,
  ) => {
    if (props.mode === 'select') return
    setErrorMessage(null)
    setPendingId(nextParentId ?? '__ws_default__')
    try {
      const outcome = await props.onReparent(
        nextParentId,
        windowOverride !== undefined ? { window: windowOverride } : undefined,
      )
      if (outcome.status === 'saved') {
        setClampPrompt(null)
        onClose()
        return
      }
      if (outcome.status === 'needs-clamp') {
        setClampPrompt({ parentId: nextParentId, clampTo: outcome.clampTo, reason: outcome.reason })
        return
      }
      setErrorMessage(outcome.reason)
    } finally {
      setPendingId(null)
    }
  }

  return (
    <div
      ref={rootRef}
      className={['chkParentPicker', props.mode === 'select' && props.multiSelect ? 'chkParentPicker--multi' : ''].filter(Boolean).join(' ')}
      role="region"
      aria-label="Change parent"
    >
      <div className="chkParentPicker__searchRow">
        <TextInput
          autoFocus
          value={query}
          onChange={setQuery}
          placeholder="Search goals, tasks, subtasks"
          className="chkParentPicker__search"
          aria-label="Search goals, tasks, subtasks"
        />
      </div>

      {props.mode !== 'select' && clampPrompt && (
        <div className="chkParentPicker__clampBanner" role="alert">
          <div className="chkParentPicker__clampBannerText">
            {clampPrompt.reason}
          </div>
          <div className="chkParentPicker__clampBannerActions">
            <Button
              variant="primary"
              size="sm"
              className="chkParentPicker__clampButton"
              onClick={() => void applySelection(clampPrompt.parentId, clampPrompt.clampTo)}
            >
              Clamp to {clampPrompt.clampTo.start} → {clampPrompt.clampTo.end}
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              className="chkParentPicker__clampDismiss"
              onClick={() => setClampPrompt(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {props.mode !== 'select' && errorMessage && !clampPrompt && (
        <div className="chkParentPicker__error" role="alert">
          {errorMessage}
        </div>
      )}

      {props.mode !== 'select' && eligibleCount === 0 && (
        <div className="chkParentPicker__empty">
          No eligible parent for this date range and book type. Change the dates, switch the book, or pick a different range on the calendar.
        </div>
      )}

      <ul className="chkParentPicker__list" role="listbox">
        {filteredRows.map((row) => {
          const isWs = row.element === null
          const rowId = isWs
            ? (props.mode !== 'select' ? props.context.workspaceDefaultContainerId : '')
            : row.element!.id
          const label = isWs ? 'Workspace (no parent)' : row.element!.title || 'Untitled'
          const kindLabel = isWs
            ? '—'
            : KIND_LABEL[row.element!.type] ?? 'Item'
          const rangeText = !isWs && row.element!.startDate && row.element!.endDate
            ? `${(row.element!.startDate as string).slice(0, 10)} → ${(row.element!.endDate as string).slice(0, 10)}`
            : ''
          const isCurrent = props.mode !== 'select'
            ? rowId === props.context.currentParentId
            : rowId === props.currentItemId
          const disabled = props.mode !== 'select' && !row.verdict.ok
          const busy = pendingId === (isWs ? '__ws_default__' : rowId)
          const isSelected = props.mode === 'select' && props.multiSelect
            ? selectedItems.some((item) => item.id === rowId)
            : false
          const atSelectionCap = props.mode === 'select' && props.multiSelect
            && props.maxSelect !== undefined && selectedItems.length >= props.maxSelect
          const selectionDisabled = atSelectionCap && !isSelected

          return (
            <li key={rowId}>
              <Pressable
                role="option"
                aria-selected={props.mode === 'select' && props.multiSelect ? isSelected : isCurrent}
                aria-disabled={disabled || selectionDisabled}
                disabled={disabled || busy || selectionDisabled}
                title={disabled && row.verdict.ok === false ? row.verdict.reason : undefined}
                className={[
                  'chkParentPicker__row',
                  disabled ? 'chkParentPicker__row--ineligible' : '',
                  isCurrent ? 'chkParentPicker__row--current' : '',
                  props.mode === 'select' && props.multiSelect ? 'chkParentPicker__row--multi' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => {
                  if (disabled || busy || selectionDisabled) return
                  if (props.mode !== 'select') {
                    void applySelection(isWs ? null : rowId)
                    return
                  }
                  if (props.multiSelect) {
                    toggleSelectedItem(row.element!)
                    return
                  }
                  props.onSelect(row.element!)
                  onClose()
                }}
              >
                {props.mode === 'select' && props.multiSelect && (
                  <span className="chkParentPicker__checkbox" aria-hidden="true">
                    {isSelected && <CheckRoundedIcon fontSize="inherit" />}
                  </span>
                )}
                <span className="chkParentPicker__rowLabel">{label}</span>
                <span className="chkParentPicker__rowMeta">
                  <span className="chkParentPicker__rowKind">{kindLabel}</span>
                  {rangeText && <span className="chkParentPicker__rowRange">{rangeText}</span>}
                </span>
                {props.mode !== 'select' && disabled && row.verdict.ok === false && (
                  <span className="chkParentPicker__rowReason">{row.verdict.reason}</span>
                )}
              </Pressable>
            </li>
          )
        })}
      </ul>

      {props.mode === 'select' && props.multiSelect && (
        <div className="chkParentPicker__basket">
          {selectedItems.length > 0 && (
            <div className="chkParentPicker__basketChips" aria-label="Selected items">
              {selectedItems.map((item) => (
                <span key={item.id} className="chkParentPicker__basketChip">
                  {item.title || 'Untitled'}
                  <IconButton
                    variant="tertiary"
                    size="sm"
                    className="chkParentPicker__basketChipRemove"
                    label={`Remove ${item.title || 'Untitled'}`}
                    icon="×"
                    onClick={() => toggleSelectedItem(item)}
                  />
                </span>
              ))}
            </div>
          )}
          <div className="chkParentPicker__commitBar">
            {props.maxSelect !== undefined && (
              <span className="chkParentPicker__capCounter">{selectedItems.length} / {props.maxSelect} selected</span>
            )}
            <Button
              variant="primary"
              size="sm"
              className="chkParentPicker__commitBtn"
              disabled={selectedItems.length === 0}
              onClick={() => {
                props.onCommit(selectedItems)
                onClose()
              }}
            >
              {selectedItems.length === 0 ? 'Add' : `Add ${selectedItems.length} item${selectedItems.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
